import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { CalendarClock, Clock } from 'lucide-react';
import { Interview } from '@/types/models';

interface InterviewTimelineDialogProps {
  interview: Interview | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEventId?: string | null;
}

// Helper type for interview timeline events
interface InterviewTimelineEvent {
  _id?: string;
  date?: string;
  status?: string;
  description?: string;
  title?: string;
  type?: string;
  subType?: string;
  metadata?: Record<string, any>;
  createdAt?: string;
  createdBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const InterviewTimelineDialog: React.FC<InterviewTimelineDialogProps> = ({
  interview,
  open,
  onOpenChange,
  initialEventId = null,
}) => {
  if (!interview) return null;

  const getStatusString = (status: any) => {
    if (!status) return '';
    if (typeof status === 'string') return status;
    if (typeof status === 'object' && status !== null && 'name' in status) return status.name;
    return String(status);
  };

  const formatStatus = (status: any) => {
    const statusStr = getStatusString(status);
    if (!statusStr) return 'Scheduled';
    return statusStr.charAt(0).toUpperCase() + statusStr.slice(1).toLowerCase();
  };

  const getCandidateName = (candidate: any) => {
    if (!candidate) return 'Unknown Candidate';
    if (typeof candidate === 'object') return `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Unknown Candidate';
    return candidate;
  };

  const getDepartmentName = (department: any) => {
    if (!department) return 'Unknown Department';
    if (typeof department === 'string') return department;
    if (department && typeof department === 'object') return department.name || 'Unknown Department';
    return 'Unknown Department';
  };



  const getTimelineLabel = (event: InterviewTimelineEvent) => {
    if (event.type === 'status_change' || event.subType === 'status_change') {
      const newStatus = event.metadata?.newStatus || event.subType;
      return formatStatus(newStatus as string);
    }
    if (event.status) {
      return formatStatus(event.status);
    }
    const lower = (event.title || '').toLowerCase();
    if (lower.includes('scheduled')) return 'Scheduled';
    if (lower.includes('confirmed')) return 'Confirmed';
    if (lower.includes('completed')) return 'Completed';
    if (lower.includes('cancelled')) return 'Cancelled';
    if (lower.includes('rescheduled')) return 'Rescheduled';
    return event.title || event.type || 'Event';
  };

  const [selectedEvent, setSelectedEvent] = React.useState<InterviewTimelineEvent | null>(null);

  React.useEffect(() => {
    if (!interview || !interview.timeline) {
      setSelectedEvent(null);
      return;
    }
    if (initialEventId) {
      const ev = interview.timeline.find(e => (e as any)._id?.toString() === initialEventId);
      if (ev) {
        setSelectedEvent(ev);
        return;
      }
    }
    // If no initialEventId, don't set a selected event (show all events)
    setSelectedEvent(null);
  }, [interview, initialEventId]);

  let timelineEvents: InterviewTimelineEvent[] = interview.timeline ? [...interview.timeline].reverse() : [];

  // Only filter duplicates if we have a specific initial event
  // If no initial event (View all clicked), show all events
  if (initialEventId) {
    // Keep only first occurrence per label to avoid repeats
    const unique: InterviewTimelineEvent[] = [];
    const seen = new Set<string>();
    for (const ev of timelineEvents) {
      const label = getTimelineLabel(ev);
      if (!seen.has(label)) {
        seen.add(label);
        unique.push(ev);
      }
    }
    timelineEvents = unique;
  }
  // If no initialEventId (View all clicked), keep all events

  const getEventDate = (event: InterviewTimelineEvent) => {
    if (event.date) return new Date(event.date).toLocaleDateString();
    if (event.createdAt) return new Date(event.createdAt).toLocaleDateString();
    return 'No date';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-500" />
            {selectedEvent ? 'Interview Timeline Event Details' : 'Interview Timeline - All Events'}
          </DialogTitle>
          <DialogDescription className="sr-only">Chronological list of events and status updates for this interview.</DialogDescription>
        </DialogHeader>

        {/* Interview summary */}
        <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <CalendarClock className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold">
              {getCandidateName(interview.candidate)}
            </h3>
            <p className="text-sm text-gray-500">{getDepartmentName(interview.department)} • {interview.interviewType}</p>
          </div>
        </div>

        {selectedEvent ? (
          <div className="space-y-6">
            {/* Event chip */}
            <div className="bg-white border rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                <span className="font-medium">{getTimelineLabel(selectedEvent)}</span>
              </div>
              <Badge variant="outline" className="bg-gray-50">
                {getEventDate(selectedEvent)}
              </Badge>
            </div>

            {/* Description */}
            {selectedEvent.description && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-sm text-gray-700 whitespace-pre-line">{selectedEvent.description}</p>
                {selectedEvent.createdBy && (
                  <p className="text-xs text-gray-500 mt-2">
                    by {selectedEvent.createdBy.firstName} {selectedEvent.createdBy.lastName}
                  </p>
                )}
              </div>
            )}

            {/* Additional Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Additional Details</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Previous Status:</span>
                  <span className="text-gray-800">{getStatusString(selectedEvent.metadata?.previousStatus ?? selectedEvent.metadata?.oldStatus) || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">New Status:</span>
                  <span className="text-gray-800">{getStatusString(selectedEvent.metadata?.newStatus ?? selectedEvent.metadata?.status ?? selectedEvent.subType ?? selectedEvent.status) || 'N/A'}</span>
                </div>
                {selectedEvent.metadata && Object.entries(selectedEvent.metadata)
                  .filter(([k]) => !['previousStatus', 'oldStatus', 'newStatus', 'status'].includes(k))
                  .map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                      <span className="text-gray-800">{String(value)}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          /* Timeline list view - show all events */
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              Showing all {timelineEvents.length} timeline events
            </div>
            {timelineEvents.map((event, index) => (
              <div key={`${event._id || index}_${event.createdAt}`} className="bg-white border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedEvent(event)}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                    <span className="font-medium">{getTimelineLabel(event)}</span>
                  </div>
                  <Badge variant="outline" className="bg-gray-50 text-xs">
                    {getEventDate(event)}
                  </Badge>
                </div>
                {event.description && (
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                    {event.description}
                  </p>
                )}
                {event.createdBy && (
                  <p className="text-xs text-gray-500 mt-1">
                    by {event.createdBy.firstName} {event.createdBy.lastName}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InterviewTimelineDialog; 